import { useMemo } from 'react'
import { Check, ChevronDown, Lock } from 'lucide-react'
import { resolvePaymentMode, type PaymentMode } from '@limo/shared/pricing'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

/**
 * Take one quote or booking out of the account's default payment mode.
 *
 * The account setting says what normally happens; this says "not for this one".
 * Shared by the quote detail page and the booking detail page so the wording,
 * the three options and the inherit semantics are identical in both places —
 * the same reason `ApplyDiscountDialog` is shared between them.
 *
 * `null` means inherit. It is a real, selectable option rather than an absence,
 * because an operator who overrides by mistake needs a way back to "whatever the
 * account does" without guessing which explicit value that is.
 */

export interface DepositModeControlProps {
  /** The stored override. `null` = inherit the account default. */
  value: PaymentMode | null
  onChange: (value: PaymentMode | null) => void
  /**
   * The account's own setting, so the inherit option can name what it resolves
   * to instead of leaving the operator to go and look it up.
   */
  accountSettings: { paymentMode?: unknown; requireDeposit?: unknown }
  /**
   * Locked once money has moved: the charge already happened, so changing what
   * "should" have been collected would only misreport it. Shown read-only
   * rather than hidden, so the operator can still see what was applied.
   */
  disabled?: boolean
  disabledReason?: string
  isSaving?: boolean
}

const MODE_LABELS: Record<PaymentMode, string> = {
  full: 'Pay in full',
  deposit: 'Pay a deposit',
  customer_choice: 'Let the customer choose',
}

const MODE_DESCRIPTIONS: Record<PaymentMode, string> = {
  full: 'This customer pays the whole amount up front.',
  deposit: 'This customer pays a deposit now, the balance at pickup.',
  customer_choice: 'This customer picks a deposit or paying in full.',
}

export function DepositModeControl({
  value,
  onChange,
  accountSettings,
  disabled = false,
  disabledReason,
  isSaving = false,
}: DepositModeControlProps) {
  const accountMode = useMemo(
    () => resolvePaymentMode({ accountSettings }),
    [accountSettings]
  )

  const effectiveMode = value ?? accountMode
  const isOverridden = value !== null

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={disabled || isSaving}>
            <Button variant="outline" size="sm" className="justify-between min-w-[220px]">
              <span className="flex items-center gap-2">
                {disabled && <Lock className="h-3 w-3 text-muted-foreground" />}
                {MODE_LABELS[effectiveMode]}
              </span>
              {!disabled && <ChevronDown className="h-4 w-4 opacity-50" />}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[300px]">
            <DropdownMenuLabel>Payment for this one</DropdownMenuLabel>
            <DropdownMenuSeparator />

            <DropdownMenuItem
              onClick={() => onChange(null)}
              className="flex flex-col items-start gap-0.5 cursor-pointer"
            >
              <span className="flex items-center gap-2 font-medium">
                {value === null && <Check className="h-3.5 w-3.5" />}
                <span className={cn(value !== null && 'ml-[22px]')}>
                  Use the account default
                </span>
              </span>
              <span className="text-xs text-muted-foreground ml-[22px]">
                Currently: {MODE_LABELS[accountMode].toLowerCase()}
              </span>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {(Object.keys(MODE_LABELS) as PaymentMode[]).map((mode) => (
              <DropdownMenuItem
                key={mode}
                onClick={() => onChange(mode)}
                className="flex flex-col items-start gap-0.5 cursor-pointer"
              >
                <span className="flex items-center gap-2 font-medium">
                  {value === mode && <Check className="h-3.5 w-3.5" />}
                  <span className={cn(value !== mode && 'ml-[22px]')}>{MODE_LABELS[mode]}</span>
                </span>
                <span className="text-xs text-muted-foreground ml-[22px]">
                  {MODE_DESCRIPTIONS[mode]}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {isOverridden && (
          <Badge variant="secondary" className="text-xs">
            Overridden
          </Badge>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        {disabled
          ? disabledReason ?? 'This can no longer be changed.'
          : isOverridden
            ? `Overriding the account default (${MODE_LABELS[accountMode].toLowerCase()}).`
            : 'Following the account default. Change it here for this one only.'}
      </p>
    </div>
  )
}
